import type { AirportBusProviderConfig } from "./types";

const KEBAB_SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export type AirportBusProviderConfigValidationIssue = {
  field: string;
  message: string;
};

/**
 * Lightweight registry checks for provider configs.
 * TypeScript already enforces the shape; this catches empty values and slug typos at boot.
 */
export function validateAirportBusProviderConfig(
  config: AirportBusProviderConfig
): AirportBusProviderConfigValidationIssue[] {
  const issues: AirportBusProviderConfigValidationIssue[] = [];

  if (typeof config.slug !== "string" || !config.slug.trim()) {
    issues.push({ field: "slug", message: "slug is required" });
  } else if (!KEBAB_SLUG.test(config.slug)) {
    issues.push({ field: "slug", message: "slug must be kebab-case (e.g. vienna-airport-bus)" });
  }

  if (typeof config.fallbackDisplayName !== "string" || !config.fallbackDisplayName.trim()) {
    issues.push({ field: "fallbackDisplayName", message: "fallbackDisplayName is required" });
  }

  if (!Array.isArray(config.passengerTypes) || config.passengerTypes.length === 0) {
    issues.push({ field: "passengerTypes", message: "passengerTypes must contain at least one option" });
  } else {
    const codes = config.passengerTypes.map(type => type.code);
    if (new Set(codes).size !== codes.length) {
      issues.push({ field: "passengerTypes", message: "passengerTypes codes must be unique" });
    }

    for (const [index, type] of config.passengerTypes.entries()) {
      if (!type?.code?.trim()) {
        issues.push({ field: `passengerTypes[${index}].code`, message: "code is required" });
      }
      if (!type?.labelKey?.trim()) {
        issues.push({ field: `passengerTypes[${index}].labelKey`, message: "labelKey is required" });
      }
    }

    if (!config.defaultPassengerTypeCode?.trim()) {
      issues.push({ field: "defaultPassengerTypeCode", message: "defaultPassengerTypeCode is required" });
    } else if (!codes.includes(config.defaultPassengerTypeCode)) {
      issues.push({
        field: "defaultPassengerTypeCode",
        message: "defaultPassengerTypeCode must match one of passengerTypes.code"
      });
    }
  }

  if (!config.i18n?.validity?.trim()) {
    issues.push({ field: "i18n.validity", message: "i18n.validity is required" });
  }
  if (!config.i18n?.nonRefundable?.trim()) {
    issues.push({ field: "i18n.nonRefundable", message: "i18n.nonRefundable is required" });
  }

  return issues;
}

export function assertValidAirportBusProviderConfig(config: AirportBusProviderConfig): void {
  const issues = validateAirportBusProviderConfig(config);
  if (issues.length > 0) {
    throw new Error(
      `Invalid AirportBusProviderConfig (${config.slug ?? "unknown"}): ${issues
        .map(issue => `${issue.field}: ${issue.message}`)
        .join("; ")}`
    );
  }
}
